import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { Icon } from '@iconify/react'
import { getRoulettePrizes, type RoulettePrize } from '@/lib/roulette'
import type { PublicRouletteDisplay, PublicRoulettePrize, LandingSpinResult } from '@/lib/landing-roulette'
import { LandingLuckyWheel } from '@/components/LandingLuckyWheel/LandingLuckyWheel'
import { displayNonePrizeHistoryLabel } from '@/lib/rouletteNoneDisplay'
import styles from './CampanhasPage.module.css'

interface RoletaPreviewProps {
  campaignId: number
  positions: number
  token: string | null
  /** Quando definido, substitui o carregamento via API e reflete o rascunho do editor em tempo real. */
  draftDisplay: PublicRouletteDisplay | null
}

function prizesToPublicDisplay(positions: number, prizes: RoulettePrize[]): PublicRouletteDisplay {
  return {
    positions,
    prizes: prizes
      .filter((p) => p.position >= 1 && p.position <= positions)
      .sort((a, b) => a.position - b.position)
      .map(
        (p): PublicRoulettePrize => ({
          position: p.position,
          prize_type: p.prize_type,
          display: (p.prize_payload ?? {}) as PublicRoulettePrize['display'],
        })
      ),
  }
}

export function RoletaPreview({ campaignId, positions, token, draftDisplay }: RoletaPreviewProps) {
  const [serverDisplay, setServerDisplay] = useState<PublicRouletteDisplay | null>(null)
  const [isLoadingServer, setIsLoadingServer] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [previewHistory, setPreviewHistory] = useState<(LandingSpinResult & { id: number; created_at: string })[]>(
    []
  )
  const hadDraftRef = useRef(false)

  useEffect(() => {
    hadDraftRef.current = false
    setServerDisplay(null)
    setPreviewHistory([])
    setError(null)
  }, [campaignId])

  const loadFromServer = useCallback(async () => {
    if (!token || positions <= 0) return
    setIsLoadingServer(true)
    setError(null)
    try {
      const data = await getRoulettePrizes(campaignId, token)
      setServerDisplay(prizesToPublicDisplay(positions, data))
    } catch {
      setError('Não foi possível carregar os prêmios para a visualização.')
      setServerDisplay(null)
    } finally {
      setIsLoadingServer(false)
    }
  }, [campaignId, token, positions])

  useEffect(() => {
    if (draftDisplay !== null) return
    void loadFromServer()
  }, [draftDisplay, loadFromServer])

  const display = useMemo(() => {
    if (draftDisplay !== null) return draftDisplay
    return serverDisplay
  }, [draftDisplay, serverDisplay])

  useEffect(() => {
    const hasDraft = draftDisplay !== null
    if (hasDraft && !hadDraftRef.current) {
      setPreviewHistory([])
    }
    hadDraftRef.current = hasDraft
  }, [draftDisplay])

  if (positions <= 0) return null

  return (
    <div className={styles.panelCard} style={{ marginTop: '20px' }}>
      <div className={styles.panelHeader}>
        <div className={styles.panelTitle}>
          <Icon icon="mdi:eye-outline" />
          <h3>Visualização da roleta</h3>
        </div>
        <p className={styles.panelSubtitle}>
          A pré-visualização acompanha automaticamente as alterações nos prêmios (antes de salvar). O giro abaixo é
          apenas simulado.
        </p>
      </div>

      <div className={styles.panelContent}>
        {error && draftDisplay === null && <p className={styles.errorText}>{error}</p>}
        {draftDisplay === null && isLoadingServer && (
          <p style={{ textAlign: 'center', color: 'var(--color-text-muted)' }}>Carregando pré-visualização...</p>
        )}

        <div
          className={styles.previewContainer}
          style={{ background: '#f8fafc', borderRadius: '12px', padding: '20px' }}
        >
          {display && (
            <LandingLuckyWheel
              campaignId={campaignId}
              publicDisplay={display}
              isPreview={true}
              onWin={(result) => {
                if (result) {
                  setPreviewHistory((prev) => [
                    { ...result, id: Date.now(), created_at: new Date().toISOString() },
                    ...prev,
                  ])
                }
              }}
            />
          )}

          {previewHistory.length > 0 && (
            <div style={{ marginTop: '30px', borderTop: '1px solid #e2e8f0', paddingTop: '20px' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '15px',
                }}
              >
                <h4 style={{ fontSize: '1.1rem', color: '#334155' }}>Histórico da sessão (teste)</h4>
                <button
                  type="button"
                  className={styles.ghostButton}
                  style={{ fontSize: '0.8rem', padding: '4px 8px' }}
                  onClick={() => setPreviewHistory([])}
                >
                  Limpar
                </button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {previewHistory.map((item) => (
                  <div
                    key={item.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px',
                      background: '#fff',
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0',
                    }}
                  >
                    <div>
                      <strong
                        style={{
                          display: 'block',
                          color: '#0f172a',
                          fontSize: item.prize_type === 'none' ? '1.85rem' : '0.95rem',
                        }}
                      >
                        {item.prize_type === 'pix' && `PIX de R$ ${Number(item.prize_payload?.amount || 0).toFixed(2)}`}
                        {item.prize_type === 'coupon' && `Cupom de ${item.prize_payload?.discount_percent}%`}
                        {item.prize_type === 'lucky_number' && `Número da sorte extra`}
                        {item.prize_type === 'none' &&
                          displayNonePrizeHistoryLabel(
                            item.prize_payload as { label?: string | null } | null | undefined
                          )}
                      </strong>
                      <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                        {new Date(item.created_at).toLocaleTimeString('pt-BR')}
                      </span>
                    </div>
                    <Icon
                      icon={item.prize_type === 'none' ? 'mdi:emoticon-sad-outline' : 'mdi:trophy-outline'}
                      style={{ fontSize: '1.4rem', color: item.prize_type === 'none' ? '#94a3b8' : '#eab308' }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
